import classNames from 'classnames';
import React, { CSSProperties, LegacyRef, useEffect, useMemo, useState } from 'react';
import {
  AspectRatio,
  Block,
  BlockContent,
  BlockLayoutExtraParams,
  BlockVerticalAlignment,
  LayoutMode,
  LinkBlockContent,
  Size,
} from './types';
import { animated, SpringConfig, SpringValues, to, useSpring, useSpringRef } from '@react-spring/web';
import { DraggableCore, DraggableData, DraggableEvent, DraggableEventHandler } from 'react-draggable';
import { PlaceholderBlock } from './components/PlaceholderBlock';
import { DocumentCoreEditor } from 'src/document-editor/core-editor';
import { Nullable } from 'src/types/nullable.type';
import { DocumentNeueCoreEditor } from 'src/document-editor/neue-core-editor';
import { BlockControlPanel } from './block-control-panel';
import { useUpdateEffect } from 'react-use';
import { ImageBlock } from './components/image-block';
import { MutualActionPlanBlock } from './mutual-action-plans/components/block';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { throttle } from 'lodash';
import { useRequestAnimationFrame } from './utils/request-animation-frame.hook';
import { useBlockStatusesStore } from './helpers/block/block-statuses-store';
import { LinkBlock } from './links/block';

const SCALE_ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 711,
  friction: 40,
};

type Props = {
  fromSectionCard?: boolean;
  block: Block;
  style:
    | SpringValues<{ x: number; y: number; width: number; height: number; scale: number; opacity: number }>
    | { x: number; y: number; width: number; height: number; scale: number; opacity: number };
  rect: { x: number; y: number; width: number; height: number };
  onStart: (id: Block['id'], e: DraggableEvent, args: DraggableData) => void;
  onDrag: (id: Block['id'], e: DraggableEvent, args: DraggableData) => void;
  onStop: (id: Block['id'], e: DraggableEvent, args: DraggableData) => void;
  onFocus: (id: Block['id']) => void;
  onBlur: (id: Block['id']) => void;
  onDelete: (id: Block['id']) => void;
  onBlockSize: (id: Block['id'], size: Size) => void;
  onUpdateBlockContent: (id: Block['id'], content: Partial<BlockContent>) => void;
  onSetVerticalAlignment: (id: Block['id'], verticalAlignment: BlockVerticalAlignment) => void;
  setContextMenuState: (id: Block['id'], pageX: number, pageY: number) => void;
  hasFocus: boolean;
  selected: boolean;
  selectionCoords: any;
  grabbing: boolean;
  contextMenuOpen: boolean;
  layoutMode: LayoutMode;
};

export const BlockView = ({
  fromSectionCard = false,
  block,
  onStart,
  onDrag,
  onStop,
  onFocus,
  onBlur,
  onDelete,
  onBlockSize,
  onUpdateBlockContent,
  onSetVerticalAlignment,
  setContextMenuState,
  hasFocus,
  selected,
  selectionCoords,
  grabbing,
  contextMenuOpen,
  style,
  rect,
  layoutMode,
}: Props) => {
  const [ref, setRef] = useState<HTMLDivElement>();
  const rectKey = `${rect.x}-${rect.y}-${rect.width}-${rect.height}`;
  const draggingActiveRef = React.useRef(false);

  const blockStatuses = useBlockStatusesStore((state) => state.blockStatuses);
  // useEffect(() => {
  //   console.log('BlockView rerendered');
  // }, [block, hasFocus, selected, selectionCoords, grabbing, contextMenuOpen, style, rect]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'block');
  // }, [block]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'hasFocus');
  // }, [hasFocus]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'selected');
  // }, [selected]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'selectionCoords');
  // }, [selectionCoords]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'grabbing');
  // }, [grabbing]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'contextMenuOpen');
  // }, [contextMenuOpen]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'style', fromSectionCard);
  // }, [style]);

  // useEffect(() => {
  //   console.log('BlockView rerendered', 'rect');
  // }, [rect]);

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const cr = entry.borderBoxSize;
      onBlockSize(block.id, { width: cr[0].inlineSize, height: cr[0].blockSize });
    });

    if (ref) {
      ro.observe(ref);
    }
    return () => {
      ro.disconnect();
    };
  }, [ref]);

  const useActiveStyle = hasFocus || selected;

  const scaleElRef = React.useRef<HTMLDivElement>(null);
  const [scaleOrigin, setScaleOrigin] = useState<{ x: number; y: number }>();

  const runOnRaf = useRequestAnimationFrame();

  const onBlockStart: DraggableEventHandler = (e, data) => {
    draggingActiveRef.current = true;
    const { clientX, clientY } = e as MouseEvent;
    if (scaleElRef.current) {
      const { left, top } = scaleElRef.current.getBoundingClientRect();
      const offsetX = clientX - left;
      const offsetY = clientY - top;
      setScaleOrigin({ x: offsetX, y: offsetY });
    }
    onStart(block.id, e, data);
  };
  const onBlockDrag: DraggableEventHandler = useRefCallback((e, data) => {
    runOnRaf(() => {
      if (draggingActiveRef.current) {
        onDrag(block.id, e, data);
      }
    });
  });

  // const throttledOnBlockDrag = useMemo(
  //   () => throttle((e: DraggableEvent, args: DraggableData) => onBlockDrag(e, args), 17),
  //   [onBlockDrag]
  // );
  const onBlockStop: DraggableEventHandler = (e, data) => {
    // setScaleOrigin(undefined);
    draggingActiveRef.current = false;
    onStop(block.id, e, data);
  };

  const api = useSpringRef();
  const [props] = useSpring(
    () => ({
      ref: api,
      from: { scale: 1 },
      to: { scale: 0.8 },
    }),
    []
  );

  useUpdateEffect(() => {
    if (grabbing) {
      console.log('transforming starts');
      api.start({
        config: SCALE_ANIMATION_CONFIG,
        to: { scale: 0.8 },
      });
    } else {
      api.start({
        config: SCALE_ANIMATION_CONFIG,
        to: { scale: 1 },
      });
    }
  }, [grabbing]);

  const isBlockLoading = (block: Block) => {
    if (blockStatuses[block.id] && blockStatuses[block.id].status === 'in-progress') {
      return true;
    }
    
    return false;
  }

  // console.log('block view content', block.content);

  return (
    <div className='absolute'>
      <DraggableCore onStop={onBlockStop} onDrag={onBlockDrag} onStart={onBlockStart} disabled={hasFocus}>
        {/* <div
          className={classNames('origin-center', {
            'scale-[0.8]': grabbing,
          })}
          style={{
            transformOrigin: scaleOrigin ? `${scaleOrigin.x}px ${scaleOrigin.y}px` : 'center',
          }}
          ref={scaleElRef}
        > */}
        <animated.div
          ref={scaleElRef}
          className={classNames(
            'group absolute flex-none rounded-2xl origin-center',
            'transition-[color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,filter,backdrop-filter]',
            'hover:bg-bedrock-light-gray',
            {
              'bg-bedrock-light-gray': contextMenuOpen,
              'bg-transparent': !contextMenuOpen,
              'neue-block-selection-ring-selected': useActiveStyle,
              'z-neue-grid-block-selected': selected,
              'z-neue-grid-block-dragging': grabbing,
              'z-neue-grid-block': !selected && !grabbing,
              'cursor-grabbing opacity-80': grabbing,
              'cursor-default': !grabbing,
              'bg-white': selected,
            }
          )}
          // style={style}
          style={{
            transformOrigin: scaleOrigin ? `${scaleOrigin.x}px ${scaleOrigin.y}px` : 'center',
            width: style.width,
            height: style.height,
            opacity: to([style.opacity, grabbing], (opacity, grabbing) => (grabbing ? 0.8 : opacity)),
            transform: to([style.x, style.y, style.scale, props.scale], (x, y, scaleOuter, scaleInner) => {
              // console.log('transforming', x, y, scaleOuter, scaleInner);
              return `translate3d(${x}px, ${y}px, 0) scale(${scaleOuter * scaleInner})`;
            }),
          }}
          onContextMenu={(e) => {
            if (block.content.type === 'text' && selected) return;
            e.preventDefault(); // prevent the default behaviour when right clicked
            console.log('Right Click');
            setContextMenuState(block.id, e.pageX, e.pageY);
          }}
        >
          {block.content.type === 'placeholder' ? (
            <PlaceholderBlock
              loading={isBlockLoading(block)}
              selected={selected}
              placeholderType={block.content.placeholderType}
              onDelete={() => onDelete(block.id)}
            />
          ) : block.content.type === 'text' ? (
            <div
              className={classNames('w-full h-full transition rounded-2xl', {
                'bg-bedrock-light-gray': contextMenuOpen,
                'bg-white': !contextMenuOpen,
                'neue-block-selection-ring-selected': useActiveStyle,
                'hover:bg-bedrock-light-gray': !useActiveStyle,
              })}
            >
              <DocumentNeueCoreEditor
                editable
                rect={rect}
                content={block.content.value}
                selected={selected}
                selectionCoords={selectionCoords}
                hasFocus={hasFocus}
                verticalAlignment={block.layout.verticalAlign}
                horizontalAlignment={block.content.horizontalAlignment}
                onTextSize={(width, height) => onBlockSize(block.id, { width, height })}
                onSetVerticalAlignment={(alignment) => onSetVerticalAlignment(block.id, alignment)}
                onSetHorizontalAlignment={(horizontalAlignment) => {
                  if (block.content.type === 'text') {
                    onUpdateBlockContent(block.id, { horizontalAlignment });
                  }
                }}
                onUpdate={(html, json) => {
                  console.log('block view editor HTML', html);
                  // const newText = json.content[0].content?.[0].text || '';
                  console.log('block content', block.content);
                  if (block.content.type === 'text') {
                    // 'if' for typing only
                    onUpdateBlockContent(block.id, {
                      value: html,
                      valueJSON: json,
                    });
                  }
                }}
                onFocus={() => onFocus(block.id)}
                onBlur={() => onBlur(block.id)}
                onDelete={() => onDelete(block.id)}
                layoutMode={layoutMode}
              />
            </div>
          ) : block.content.type === 'image' ? (
                <ImageBlock
                  block={block}
                  selected={selected}
                  forceHoverState={contextMenuOpen}
                  onDelete={() => onDelete(block.id)}
                />
          ) : (
            block.content.type === 'mutual-action-plan' ? (
            <MutualActionPlanBlock
              key={`mutual-action-plan-block-${block.id}`}
              block={block}
              uuid={block.content.uuid}
              selected={selected}
              editable={true}
              contextMenuOpen={contextMenuOpen}
              useActiveStyle={useActiveStyle}
              onBlockSizeChange={(width, height) => onBlockSize(block.id, { width, height })} />
            ) : (
              block.content.type === 'link' ? (
                <LinkBlock
                  block={block}
                  selected={selected}
                  editable={true}
                  useActiveStyle={useActiveStyle}
                  forceHoverState={contextMenuOpen}
                  onBlockSizeChange={(width, height) => onBlockSize(block.id, { width, height })}
                  onUpdateLinkTitle={(title: string) => {
                    onUpdateBlockContent(block.id, {
                      link: {
                        ...((block.content as LinkBlockContent).link),
                        title,
                      }
                    });
                  }}
                  onUpdateLinkCta={(cta: string) => {
                    onUpdateBlockContent(block.id, {
                      cta
                    });
                  }}
                />
              ) : null
            )
          )}
        </animated.div>
        {/* </div> */}
      </DraggableCore>
    </div>
  );
};
